import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { DetailedWithdrawal } from '@starsoft/common/models/withdraw/detailed';
import { AxiosError } from 'axios';
import {
  CoinID,
  CommonModalsKey,
  RedirectError,
  WithdrawStep,
} from '../../enums';
import { GenericError } from '../../models';
import { useModalNavigation } from '../modals/useModalNavigation';
import { useQueryDefaults } from '../react-query/useQueryDefaults';
import { useNavigate } from '../useNavigate';
import { useSearchParams } from '../useSearchParams';

// Interface for the variables used in the onError function
interface Variables {
  coinId: number; // The ID of the coin involved in the withdrawal process
}

/**
 * Custom hook to handle withdrawal callbacks.
 * This hook provides functions to handle success and error scenarios during a withdrawal process.
 *
 * @returns {Object} An object containing the onError and onSuccess functions.
 *
 * @example
 * // Usage example:
 * const { onError, onSuccess } = useWithdrawCallbacks();
 * onSuccess(withdrawData); // Handle successful withdrawal
 * onError(error, variables, context); // Handle error during withdrawal
 */
export function useWithdrawCallbacks() {
  // Hook to navigate to different routes
  const navigate: NavigateFunction = useNavigate();
  // Hook to navigate to different modals
  const { navigateToModal } = useModalNavigation();
  // Hook to retrieve current search parameters
  const searchParams: URLSearchParams = useSearchParams();
  // Hook to get default error handling function
  const { onError: defaultOnError } = useQueryDefaults();

  /**
   * Function to handle successful withdrawal.
   * Updates the URL search parameters and navigates to the withdrawal modal.
   *
   * @param {DetailedWithdrawal} withdraw - The withdrawal object containing withdrawal details.
   */
  function onSuccess(withdraw: DetailedWithdrawal) {
    const newUrlSearchParams = new URLSearchParams(searchParams);
    newUrlSearchParams.set('withdraw-id', `${withdraw.id}`); // Set the withdrawal ID in the search params
    newUrlSearchParams.set('step', WithdrawStep.Response); // Set the step to Response

    // Navigate to the Withdraw modal with updated search parameters
    navigateToModal(
      CommonModalsKey.Withdraw,
      `?${newUrlSearchParams.toString()}`,
    );
  }

  /**
   * Function to handle errors during withdrawal.
   * Handles specific error scenarios and navigates accordingly.
   *
   * @param {AxiosError<GenericError>} error - The error object from Axios.
   * @param {Variables | unknown} variables - The variables used in the request, including coinId.
   * @param {unknown} context - Additional context for the error handling.
   */
  function onError(
    error: AxiosError<GenericError>,
    variables: Variables | unknown,
    context: unknown,
  ) {
    // Check if the error status code is not 307
    if (error.response?.data?.statusCode !== 307) {
      defaultOnError?.(error, variables, context); // Call default error handler
      return;
    }

    // Handle specific error message for uncompleted KYC
    if (error.response?.data.message == RedirectError.UNCOMPLETED_KYC) {
      defaultOnError?.(error, variables, context); // Call default error handler
      navigate('/account/verification/?kyc-tab=status'); // Navigate to KYC verification page
      return;
    }

    // Handle specific error message for uncompleted rollover
    if (error.response?.data.message == RedirectError.UNCOMPLETED_ROLLOVER) {
      defaultOnError?.(error, variables, context); // Call default error handler

      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set(
        'coin_id',
        String((variables as Variables)?.coinId ?? CoinID.BRL), // Set coin ID in search params
      );
      // Navigate to the Rollover Required modal with updated search parameters
      navigateToModal(
        CommonModalsKey.RolloverRequired,
        `?${urlSearchParams.toString()}`,
      );
      return;
    }
  }

  return {
    onError, // Return the onError function
    onSuccess, // Return the onSuccess function
  };
}
